// ─── What: chart panel geometry ───────────────────────────────────────────────
//
// Extracted verbatim from ConvergenceChart's render effect. This is pure
// arithmetic over "which panels exist, which are open, how much room is there"
// and it was previously interleaved with theming, scale construction and
// drawing, which is why nothing could test it.
//
// The rules it encodes, stated once:
//
//   - The probability panel always exists. Volume, position and conviction are
//     sub-panels; position and conviction only EXIST when their data does.
//   - Every panel that EXISTS reserves a label row, whether or not it is open.
//     That is what keeps the collapsed toggles clickable in place.
//   - The sub-zone's content budget is fixed by which panels exist, and is then
//     split EQUALLY among the ones currently open. So closing one panel makes
//     the others taller rather than shrinking the chart.
//   - The sub-zone content collapses to nothing only when every sub-panel is
//     closed; the label rows survive.

import { rightAxisMargin, type Margin } from '../chartAxis';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MARGIN: Margin = rightAxisMargin({
    top: 28,
    right: 12,
    bottom: 8,
    left: 40,
});

export const PROB_HEIGHT = 200;
export const VOL_HEIGHT = 60;
export const POS_HEIGHT = 60;
export const CONVICTION_HEIGHT = 100;
export const GAP = 16;

/** Height of the loading skeleton / initial SVG, before layout runs. */
export const TOTAL_HEIGHT = PROB_HEIGHT + GAP + VOL_HEIGHT;

/** Headroom above 100% / below 0% so markers at the extremes aren't clipped. */
export const Y_EDGE_PAD = 14;

/** Signal and alert markers overflow the plot rect by up to this much. */
export const MARKER_OVERFLOW = 25;

/** Reserved per existing panel for its separator + clickable label. */
const LABEL_ROW = GAP;

/** Fullscreen grows the sub-zone budget without changing the split rules. */
const FULLSCREEN_SCALE = 1.55;

/** The probability panel never collapses below this, even in fullscreen. */
const MIN_PROB_HEIGHT = 200;

/** Chrome below the SVG inside the fullscreen container (header strip etc). */
const FULLSCREEN_CHROME = 40;

// ─── Input / output ───────────────────────────────────────────────────────────

export interface LayoutInput {
    /** Measured container width in px. */
    width: number;
    fullscreen: boolean;
    /**
     * containerRef.current.clientHeight, or null when unmeasured. Only consulted
     * in fullscreen, where the probability panel absorbs the leftover height.
     */
    containerHeight: number | null;
    /** A position panel exists (some trader is highlighted). */
    hasPositionChart: boolean;
    /** A conviction panel exists (conviction series carry points). */
    hasConvictionPanel: boolean;
    /** Toggle state. Position/conviction are ANDed with their existence. */
    showVol: boolean;
    showPos: boolean;
    showConviction: boolean;
    /** Asset overlays that actually loaded data — they widen the right gutter. */
    loadedAssetCount: number;
}

export interface ChartClipIds {
    prob: string;
    vol: string;
    pos: string;
    conv: string;
    /**
     * Signal markers overflow the plot rect, so they get their own looser clip.
     */
    sig: string;
    convSig: string;
}

export interface ChartLayout {
    margin: Margin;
    /** Plot width, gutters excluded. Can be negative on a too-narrow container. */
    w: number;

    volVisible: boolean;
    posVisible: boolean;
    convVisible: boolean;

    probHeight: number;
    volHeight: number;
    posHeight: number;
    convHeight: number;

    volTop: number;
    volBottom: number;
    posTop: number;
    posBottom: number;
    convTop: number;
    convBottom: number;

    /** SVG height. */
    totalHeight: number;

    clipIds: ChartClipIds;
}

// ─── How it works ─────────────────────────────────────────────────────────────

/** Widen the right gutter to fit one or two asset price axes. */
function rightGutter(loadedAssetCount: number): number {
    if (loadedAssetCount === 0) return MARGIN.right;
    return loadedAssetCount > 1 ? 96 : 52;
}

/**
 * Clip-path ids must be unique per document, not per chart: two ConvergenceCharts
 * on one page would otherwise share a definition and the second would clip to the
 * first's geometry. Generated once per draw pass rather than per panel so the ids
 * in the DOM read as one coherent set.
 */
export function makeClipIds(): ChartClipIds {
    const suffix = Math.random().toString(36).slice(2, 8);
    return {
        prob: `prob-clip-${suffix}`,
        vol: `vol-clip-${suffix}`,
        pos: `pos-clip-${suffix}`,
        conv: `conv-clip-${suffix}`,
        sig: `sig-clip-${suffix}`,
        convSig: `conv-sig-clip-${suffix}`,
    };
}

export function computeLayout(input: LayoutInput): ChartLayout {
    const {
        width,
        fullscreen,
        containerHeight,
        hasPositionChart,
        hasConvictionPanel,
        showVol,
        showPos,
        showConviction,
        loadedAssetCount,
    } = input;

    // Which panels are toggled on. Volume always exists; the other two are
    // gated on having anything to draw.
    const volVisible = showVol;
    const posVisible = showPos && hasPositionChart;
    const convVisible = showConviction && hasConvictionPanel;

    const existingPanelCount =
        1 + (hasPositionChart ? 1 : 0) + (hasConvictionPanel ? 1 : 0);
    const visiblePanelCount =
        (volVisible ? 1 : 0) + (posVisible ? 1 : 0) + (convVisible ? 1 : 0);

    const anySubPanelExists = existingPanelCount >= 1;
    const anySubPanelVisible = visiblePanelCount > 0;

    // Always reserved, so a collapsed panel keeps its toggle in place.
    const labelsHeight = existingPanelCount * LABEL_ROW;

    const baseSubzoneContent =
        VOL_HEIGHT +
        (hasPositionChart ? POS_HEIGHT : 0) +
        (hasConvictionPanel ? CONVICTION_HEIGHT : 0);
    const subzoneContentBudget = Math.round(
        baseSubzoneContent * (fullscreen ? FULLSCREEN_SCALE : 1)
    );

    // Equal share among the open panels; closed ones get no content height.
    const perPanelContent =
        visiblePanelCount > 0
            ? Math.floor(subzoneContentBudget / visiblePanelCount)
            : 0;
    const volHeight = volVisible ? perPanelContent : 0;
    const posHeight = posVisible ? perPanelContent : 0;
    const convHeight = convVisible ? perPanelContent : 0;

    const subzoneTotal = anySubPanelExists
        ? anySubPanelVisible
            ? labelsHeight + subzoneContentBudget
            : labelsHeight
        : 0;

    // Fixed normally; in fullscreen the probability panel takes whatever the
    // sub-zone did not claim.
    const probHeight =
        fullscreen && containerHeight != null
            ? Math.max(
                  MIN_PROB_HEIGHT,
                  containerHeight - subzoneTotal - FULLSCREEN_CHROME
              )
            : PROB_HEIGHT;

    const w = width - MARGIN.left - rightGutter(loadedAssetCount);

    // Every panel that exists gets its LABEL_ROW before its content, so the
    // coordinates advance even through a closed panel.
    const volTop = probHeight + LABEL_ROW;
    const volBottom = volTop + volHeight;
    const posTop = hasPositionChart ? volBottom + LABEL_ROW : volBottom;
    const posBottom = posTop + posHeight;
    const convTop = hasConvictionPanel ? posBottom + LABEL_ROW : posBottom;
    const convBottom = convTop + convHeight;

    return {
        margin: MARGIN,
        w,
        volVisible,
        posVisible,
        convVisible,
        probHeight,
        volHeight,
        posHeight,
        convHeight,
        volTop,
        volBottom,
        posTop,
        posBottom,
        convTop,
        convBottom,
        // NB: derived from the budget, not from convBottom. Flooring the equal
        // split can leave a pixel or two of slack at the bottom of the sub-zone.
        totalHeight: probHeight + subzoneTotal,
        clipIds: makeClipIds(),
    };
}
